/**
 * Web: Firebase JS SDK `firebase/messaging` + Service Worker `public/firebase-messaging-sw.js`
 */
import { getApp } from 'firebase/app';
import { deleteToken, getMessaging, getToken, isSupported } from 'firebase/messaging';

import { messagingVapidKey } from '../config/messagingConfig';
import {
  getCachedFcmToken,
  removeUserFcmToken,
  saveUserFcmToken,
} from './notificationService.storage';

export { getCachedFcmToken } from './notificationService.storage';

async function ensureServiceWorkerRegistered(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }
  await navigator.serviceWorker.register('/firebase-messaging-sw.js');
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return false;
  }
  const supported = await isSupported();
  if (!supported) {
    return false;
  }
  const permission = await Notification.requestPermission();
  return permission === 'granted';
}

export async function getFcmToken(): Promise<string | null> {
  const supported = await isSupported();
  if (!supported) {
    return null;
  }
  try {
    await ensureServiceWorkerRegistered();
    const messaging = getMessaging(getApp());
    const token = await getToken(messaging, {
      vapidKey: messagingVapidKey,
    });
    return token || null;
  } catch {
    return null;
  }
}

export async function registerPushNotificationsForUser(uid: string): Promise<void> {
  const ok = await requestNotificationPermission();
  if (!ok) {
    return;
  }
  const token = await getFcmToken();
  if (!token) {
    return;
  }
  await saveUserFcmToken(uid, token);
}

export async function unregisterPushNotificationsForUser(uid: string): Promise<void> {
  await removeUserFcmToken(uid, getCachedFcmToken());
  try {
    const supported = await isSupported();
    if (supported) {
      const messaging = getMessaging(getApp());
      await deleteToken(messaging);
    }
  } catch {
    /* yok say */
  }
}
